/**
 * Получить значение URL параметра из текущего URL
 * Поддерживает как обычные URL (?param=value), так и HashRouter (#/path?param=value)
 * @param {string} paramName - Имя параметра для поиска
 * @returns {string|null} Значение параметра или null если не найден
 */
export const getUrlParam = (paramName) => {
  try {
    // Для HashRouter параметры находятся после # в hash
    // URL: http://localhost:5173/#/cars?param=value
    // window.location.hash: "#/cars?param=value"
    // window.location.search: "" (пусто!)

    let searchString = '';

    // Проверяем есть ли параметры в hash (для HashRouter)
    if (window.location.hash && window.location.hash.includes('?')) {
      // Извлекаем query string из хеша: "#/cars?param=value" -> "param=value"
      searchString = window.location.hash.split('?')[1];
    } else if (window.location.search) {
      // Обычный URL с параметрами до хеша
      searchString = window.location.search.substring(1); // Убираем "?"
    }

    const searchParams = new URLSearchParams(searchString);
    return searchParams.get(paramName);
  } catch (e) {
    console.error(`[urlParams] Error getting URL param "${paramName}":`, e);
    return null;
  }
};

/**
 * Получить все конфигурационные параметры из URL
 * Поддерживаемые параметры: inventoryId, agentId (no workflow since gitlab#1576)
 *
 * @returns {Object} Объект с найденными параметрами
 *
 * @example
 * // URL: http://localhost:5173/?inventoryId=custom_inventory
 * const params = getConfigFromUrl();
 * // { inventoryId: 'custom_inventory', agentId: null }
 */
export const getConfigFromUrl = () => {
  const inventoryId = getUrlParam('inventoryId');
  const agentId = getUrlParam('agentId');
  // organizationId/inventorySlug в URL остались для обратной совместимости
  // и dev-переходов (например прямой open /cars/<id>/print?organizationId=1).
  // Production-flow использует ?token (см. services/showroomTokenResolver.js)
  // — он привязан к dealership и закрывает перебор carId.
  const organizationId = getUrlParam('organizationId');
  const inventorySlug = getUrlParam('inventorySlug');

  // NOTE: Sentry DSN намеренно НЕ читается из URL параметров по соображениям безопасности.
  // DSN должен передаваться только через runtimeConfig, env vars или B2BDemo.mount()

  const result = {
    inventoryId,
    agentId,
    organizationId,
    inventorySlug,
  };

  // Логируем только если хотя бы один параметр найден
  if (inventoryId || agentId) {
    console.log('[urlParams] Config parameters from URL:', result);
  }

  return result;
};

/**
 * Получить конфигурационные параметры из data-атрибутов iframe
 * Поддерживаемые атрибуты: data-inventory-id, data-agent-id
 *
 * @returns {Object} Объект с найденными параметрами
 *
 * @example
 * // <iframe data-inventory-id="custom_inventory">
 * const params = getConfigFromIframeDataAttributes();
 * // { inventoryId: 'custom_inventory', agentId: null }
 */
export const getConfigFromIframeDataAttributes = () => {
  try {
    // Проверяем находимся ли мы внутри iframe
    if (typeof window === 'undefined' || window.self === window.top) {
      return { inventoryId: null, agentId: null };
    }

    // Получаем элемент iframe
    const frameElement = window.frameElement;
    if (!frameElement) {
      return { inventoryId: null, agentId: null };
    }

    // Читаем data-атрибуты
    const inventoryId = frameElement.getAttribute('data-inventory-id');
    const agentId = frameElement.getAttribute('data-agent-id');

    // NOTE: Sentry DSN намеренно НЕ читается из iframe data-атрибутов по соображениям безопасности.

    const result = {
      inventoryId,
      agentId,
    };

    // Логируем только если хотя бы один параметр найден
    if (inventoryId || agentId) {
      console.log('[urlParams] Config parameters from iframe data-attributes:', result);
    }

    return result;
  } catch (e) {
    // Ошибка может возникнуть при cross-origin iframe
    console.warn('[urlParams] Cannot read iframe data-attributes (cross-origin?):', e.message);
    return { inventoryId: null, agentId: null };
  }
};

/**
 * Маппинг URL параметров на CSS custom properties
 * Позволяет передавать стилевые параметры через URL
 */
const THEME_PARAM_MAPPING = {
  // Colors
  primaryColor: '--theme-primary',
  primaryHover: '--theme-primary-hover',
  accentColor: '--theme-accent',
  bgColor: '--theme-bg-primary',
  textColor: '--theme-text-primary',
  specialOffersColor: '--theme-special-offers',

  // Typography
  fontFamily: '--theme-font-family',
  fontSize: '--theme-font-size-base',

  // Borders & Radius
  borderRadius: '--radius',
  cardRadius: '--theme-card-radius',
  buttonRadius: '--theme-button-radius',

  // Spacing
  spacingSm: '--theme-spacing-sm',
  spacingMd: '--theme-spacing-md',
  spacingLg: '--theme-spacing-lg',
};

/**
 * Получить стилевые параметры из URL для кастомизации темы
 *
 * @returns {Object} Объект с CSS переменными и их значениями
 *
 * @example
 * // URL: ?primaryColor=#FF5733&fontFamily=Arial&borderRadius=12px
 * const theme = getThemeFromUrl();
 * // {
 * //   '--theme-primary': '#FF5733',
 * //   '--theme-font-family': 'Arial',
 * //   '--theme-border-radius': '12px'
 * // }
 */
export const getThemeFromUrl = () => {
  const theme = {};
  let hasThemeParams = false;

  // Проходим по всем поддерживаемым параметрам
  Object.entries(THEME_PARAM_MAPPING).forEach(([paramName, cssVarName]) => {
    const value = getUrlParam(paramName);
    if (value) {
      theme[cssVarName] = value;
      hasThemeParams = true;
    }
  });

  // Логируем только если есть стилевые параметры
  if (hasThemeParams) {
    console.log('[urlParams] Theme parameters from URL:', theme);
  }

  return theme;
};

/**
 * Получить флаг showRegText из URL параметров или iframe data-атрибутов
 * Используется для отображения "+ reg" рядом с ценой
 *
 * @returns {boolean} true если нужно показывать "+ reg"
 *
 * @example
 * // URL: ?showRegText=true
 * // Или: <iframe data-show-reg-text="true">
 * const shouldShowReg = getShowRegText();
 * // true
 */
export const getShowRegText = () => {
  // Проверяем URL параметр
  const urlParam = getUrlParam('showRegText');
  if (urlParam !== null) {
    return urlParam === 'true' || urlParam === '1';
  }

  // Проверяем iframe data-атрибут
  try {
    if (typeof window !== 'undefined' && window.self !== window.top) {
      const frameElement = window.frameElement;
      if (frameElement) {
        const dataAttr = frameElement.getAttribute('data-show-reg-text');
        if (dataAttr !== null) {
          return dataAttr === 'true' || dataAttr === '1';
        }
      }
    }
  } catch (e) {
    // Cross-origin iframe - не можем прочитать атрибуты
  }

  // По умолчанию не показываем
  return false;
};
